import { ReactNode } from 'react';
import { getVaultTopicIcon } from '~/routing/vaultTopicIcons';

interface EmptyStateProps {
  icon?: ReactNode;
  /**
   * Vault topic pageKey. When set (and no explicit `icon` is passed) the empty
   * state renders that topic's icon from the single source of truth, keeping it
   * in sync with the sidebar and dashboard (MCV-1495).
   */
  pageKey?: string;
  title?: string;
  description?: string;
  action?: ReactNode;
  cyTag?: string;
}

const DefaultIcon = () => (
  <svg
    className="size-10 text-grey-700"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M2.25 13.5h3.86a2.25 2.25 0 0 1 2.012 1.244l.256.512a2.25 2.25 0 0 0 2.013 1.244h3.218a2.25 2.25 0 0 0 2.013-1.244l.256-.512a2.25 2.25 0 0 1 2.013-1.244h3.859m-19.5.338V18a2.25 2.25 0 0 0 2.25 2.25h15A2.25 2.25 0 0 0 21.75 18v-4.162c0-.224-.034-.447-.1-.661L19.24 5.338a2.25 2.25 0 0 0-2.15-1.588H6.911a2.25 2.25 0 0 0-2.15 1.588L2.35 13.177a2.25 2.25 0 0 0-.1.661Z"
    />
  </svg>
);

export const EmptyState = ({
  icon,
  pageKey,
  title = 'No records yet',
  description = 'Add your first entry to get started',
  action,
  cyTag,
}: EmptyStateProps) => {
  const TopicIcon = pageKey ? getVaultTopicIcon(pageKey) : undefined;
  const resolvedIcon =
    icon ?? (TopicIcon ? <TopicIcon className="size-10 text-grey-700" /> : <DefaultIcon />);

  return (
    <div data-cy={cyTag} className="flex flex-col items-center justify-center py-12 text-grey-500">
      <div className="mb-3">{resolvedIcon}</div>
      <p className="body-text-sb">{title}</p>
      {description && <p className="body-text text-sm mt-1">{description}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
};
